import { useState, useEffect, useRef, useCallback } from 'react';
import SyncManager from '../utils/SyncManager';

/**
 * React hook for managing video synchronization
 * Provides a React-friendly interface to the SyncManager utility
 * 
 * @param {Array} sessions - Array of { videoId, sessionData } objects
 * @returns {Object} Sync state and control functions
 */
export function useSyncManager(sessions = []) {
  const syncManagerRef = useRef(null);
  const [syncState, setSyncState] = useState({
    syncMode: 'lapStart'
  });
  
  // Master timeline state (independent of videos)
  // Start at 10,000s to avoid negative video times during sync
  const [timeline, setTimeline] = useState({
    currentTime: 10000,
    isPlaying: false,
    playbackRate: 1
  });
  
  // Independent timeline ticker using requestAnimationFrame
  const timelineTickerRef = useRef(null);
  const lastTickTimeRef = useRef(null);
  
  // Video control modes
  const [videoModes, setVideoModes] = useState(new Map()); // videoId -> 'sync' | 'individual'

  // Independent timeline ticker
  useEffect(() => {
    const tick = (currentTime) => {
      if (timeline.isPlaying) {
        const deltaTime = lastTickTimeRef.current 
          ? (currentTime - lastTickTimeRef.current) / 1000 * timeline.playbackRate
          : 0;
        
        setTimeline(prev => ({
          ...prev,
          currentTime: Math.max(0, prev.currentTime + deltaTime)
        }));
      }
      
      lastTickTimeRef.current = currentTime;
      timelineTickerRef.current = requestAnimationFrame(tick);
    };
    
    timelineTickerRef.current = requestAnimationFrame(tick);
    
    return () => {
      if (timelineTickerRef.current) {
        cancelAnimationFrame(timelineTickerRef.current);
      }
    };
  }, [timeline.isPlaying, timeline.playbackRate]);

  // Initialize sync manager
  useEffect(() => {
          // Set up event listener
      const handleSyncEvent = (eventType, data) => {
        // Update state based on sync events
        setSyncState(prevState => ({
          ...prevState,
          syncMode: syncManagerRef.current.syncMode
        }));
      };

    if (!syncManagerRef.current) {
      syncManagerRef.current = new SyncManager();
      syncManagerRef.current.addEventListener(handleSyncEvent);
    }

    return () => {
      if (syncManagerRef.current) {
        syncManagerRef.current.removeEventListener(handleSyncEvent);
      }
    };
  }, []);

  // Update sessions in sync manager
  useEffect(() => {
    if (!syncManagerRef.current) return;

    const manager = syncManagerRef.current;
    
    // Check if sessions have actually changed to avoid unnecessary updates
    const currentSessionIds = Array.from(manager.sessions.keys()).sort();
    const newSessionIds = sessions.map(s => s.videoId).sort();
    
    // Only update if sessions have changed
    if (JSON.stringify(currentSessionIds) !== JSON.stringify(newSessionIds)) {
          // Clear existing sessions
    manager.sessions.clear();
    manager.customOffsets.clear();
    manager.selectedLaps.clear();

    // Add new sessions and set them to sync mode
    const newVideoModes = new Map();
    sessions.forEach(({ videoId, sessionData }) => {
      if (sessionData) {
        manager.addSession(videoId, sessionData);
        newVideoModes.set(videoId, 'sync');
      }
    });

    // Update states
    setVideoModes(newVideoModes);
    setSyncState(prevState => ({
      ...prevState,
      syncMode: manager.syncMode
    }));
    }
  }, [sessions]);

  /**
   * Get current sync state for all videos
   */
  const getCurrentSyncState = useCallback((referenceVideoId) => {
    if (!syncManagerRef.current || !referenceVideoId) {
      return {
        syncMode: 'lapStart',
        timelineTime: timeline.currentTime,
        videoTimes: new Map()
      };
    }
    
    return syncManagerRef.current.getSyncState(timeline.currentTime, referenceVideoId);
  }, [timeline.currentTime]);

  /**
   * Handle lap selection change
   */
  const handleLapSelection = useCallback((videoId, lapNumber) => {
    if (!syncManagerRef.current) return;
    
    syncManagerRef.current.handleLapSelection(videoId, lapNumber);
    
    // Update state
    setSyncState(prevState => ({
      ...prevState,
      syncMode: syncManagerRef.current.syncMode
    }));
  }, []);

  /**
   * Handle individual video time change (for nudging)
   */
  const handleVideoTimeChange = useCallback((videoId, newTime, referenceVideoId) => {
    if (!syncManagerRef.current || !referenceVideoId) return;
    
    syncManagerRef.current.handleVideoTimeChange(videoId, newTime, timeline.currentTime, referenceVideoId);
    
    // Switch video to individual mode
    setVideoModes(prev => new Map(prev).set(videoId, 'individual'));
    
    // Update state
    setSyncState(prevState => ({
      ...prevState,
      syncMode: syncManagerRef.current.syncMode
    }));
  }, [timeline.currentTime]);

  /**
   * Set sync mode manually
   */
  const setSyncMode = useCallback((mode) => {
    if (!syncManagerRef.current) return;
    
    syncManagerRef.current.setSyncMode(mode);
    
    setSyncState(prevState => ({
      ...prevState,
      syncMode: mode
    }));
  }, []);

  /**
   * Set timeline state
   */
  const setTimelineState = useCallback((updates) => {
    setTimeline(prev => ({ ...prev, ...updates }));
  }, []);
  
  /**
   * Set video mode (sync or individual)
   */
  const setVideoMode = useCallback((videoId, mode) => {
    setVideoModes(prev => new Map(prev).set(videoId, mode));
  }, []);
  
  /**
   * Reset all videos back to sync mode
   */
  const resetAllToSync = useCallback(() => {
    setVideoModes(prev => {
      const newModes = new Map(prev);
      newModes.forEach((_, videoId) => {
        newModes.set(videoId, 'sync');
      });
      return newModes;
    });
  }, []);

  /**
   * Get selected lap for a video
   */
  const getSelectedLap = useCallback((videoId) => {
    if (!syncManagerRef.current) return 1;
    return syncManagerRef.current.getSelectedLap(videoId);
  }, []);

  /**
   * Get custom offset for a video
   */
  const getCustomOffset = useCallback((videoId) => {
    if (!syncManagerRef.current) return 0;
    return syncManagerRef.current.customOffsets.get(videoId) || 0;
  }, []);

  /**
   * Reset all sync state
   */
  const reset = useCallback(() => {
    if (!syncManagerRef.current) return;
    
    syncManagerRef.current.reset();
    
    // Reset timeline
    setTimeline({
      currentTime: 10000,
      isPlaying: false,
      playbackRate: 1
    });
    
    // Reset all videos to sync mode
    resetAllToSync();
    
    setSyncState(prevState => ({
      ...prevState,
      syncMode: 'lapStart'
    }));
  }, [resetAllToSync]);

  return {
    // State
    syncState,
    syncMode: syncState.syncMode,
    timeline,
    videoModes,
    
    // Functions
    getCurrentSyncState,
    handleLapSelection,
    handleVideoTimeChange,
    setSyncMode,
    setTimelineState,
    setVideoMode,
    resetAllToSync,
    getSelectedLap,
    getCustomOffset,
    reset,
    
    // Internal reference for advanced operations
    syncManagerRef
  };
}

export default useSyncManager; 